import { Link } from "react-router-dom";
import { Repeat } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";
import type { Order } from "@/types/tms";
import { useOrderCancelledBy, toUsernameHandle } from "@/hooks/useOrderCancelledBy";
import { formatCancellationTimestamp } from "@/lib/timezone";
import { useViewerTimezone } from "@/hooks/useViewerTimezone";

interface CancellationBannerProps {
  order: Order & {
    cancellation_reason?: string | null;
    cancellation_notes?: string | null;
    canceled_at?: string | null;
    external_order_id?: string | null;
    merged_into_order_id?: string | null;
    merged_at?: string | null;
    merged_by?: string | null;
  };
  /** When true, render the Multi-leg chip alongside the Canceled badge. */
  isMultiLeg?: boolean;
  /** Optional recurring template (unique_id + id) for the recurring link chip. */
  recurringTemplate?: { unique_id: string } | null;
  className?: string;
}

/**
 * Banner shown in the order preview panel and detail page when an order is in
 * the `canceled` status. Replaces the standard chip cluster (status / trip type
 * / API / recurring) and adds the cancellation reason, actor, timestamp, and
 * any free-text note entered during cancellation.
 */
export function CancellationBanner({
  order,
  isMultiLeg,
  recurringTemplate,
  className,
}: CancellationBannerProps) {
  const reason = order.cancellation_reason?.trim() || (order.merged_into_order_id ? "Merged as duplicate" : "—");
  const notes = order.cancellation_notes?.trim() || "";

  const { data: actor } = useOrderCancelledBy(order.id, order.canceled_at ?? null, true, {
    mergedIntoOrderId: order.merged_into_order_id,
    mergedAt: order.merged_at,
    mergedBy: order.merged_by,
  });
  const mergedInto = actor?.mergedInto ?? null;
  const username = toUsernameHandle(actor?.changedBy);
  const viewerTz = useViewerTimezone();
  const when = formatCancellationTimestamp(actor?.changedAt ?? order.canceled_at ?? null, viewerTz);

  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 space-y-3",
        className,
      )}
    >
      {/* Chip row */}
      <div className="flex flex-wrap items-center gap-2">
        <OrderStatusBadge status="canceled" />
        {isMultiLeg && (
          <Badge variant="secondary" className="text-xs font-medium">
            Multi-leg
          </Badge>
        )}
        {order.trip_type && (
          <Badge variant="secondary" className="text-xs font-medium">
            {order.trip_type}
          </Badge>
        )}
        {order.external_order_id && (
          <Badge variant="info" className="text-xs font-medium">
            API Order
          </Badge>
        )}
        {recurringTemplate && order.recurring_order_template_id && (
          <Link to={`/templates/${order.recurring_order_template_id}`}>
            <Badge
              variant="outline"
              className="gap-1 text-xs font-normal hover:bg-accent cursor-pointer"
            >
              <Repeat className="h-3 w-3" />
              {recurringTemplate.unique_id}
            </Badge>
          </Link>
        )}
      </div>

      {/* Reason + actor lines */}
      <div className="space-y-0.5">
        <p className="text-sm">
          <span className="font-semibold">Reason:</span>{" "}
          <span>{reason}</span>
        </p>
        {mergedInto ? (
          <p className="text-sm text-muted-foreground">
            Merged into{" "}
            <Link to={`/orders/${mergedInto.id}`} className="font-mono underline hover:text-foreground">
              {mergedInto.uniqueId ?? mergedInto.id}
            </Link>
            {actor?.changedBy ? ` by ${username}` : ""}
            {when ? ` ${when}` : ""}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Canceled by {username}
            {when ? ` ${when}` : ""}
          </p>
        )}
      </div>

      {/* Optional cancellation notes */}
      {notes && (
        <div className="rounded-md border bg-muted/50 p-3 text-sm whitespace-pre-wrap break-words">
          {notes}
        </div>
      )}
    </div>
  );
}